/**
 * SPED-Based Pricing Model for Brazilian Accounting SaaS
 * Based on fiscal regime complexity and operational volume
 */

export type RegimeTributario = 'MEI' | 'SimplesNacional' | 'LucroPresumido' | 'LucroReal';

export interface RegimeComplexity {
  regime: RegimeTributario;
  displayName: string;
  basePrice: number;
  description: string;
  obligations: string[];
  complexityLevel: 'very_low' | 'low' | 'medium' | 'high';
}

export interface VolumeFactors {
  notasFiscaisMensal: number;      // Notas fiscais emitidas/mês
  itensMediaPorNota: number;        // Média de itens por nota
  funcionariosAtivos: number;       // Funcionários (eSocial)
}

export interface PricingTier {
  minValue: number;
  maxValue: number | null;  // null = unlimited
  additionalCost: number;
  label: string;
}

// Base pricing by regime (monthly)
export const REGIME_BASE_PRICES: RegimeComplexity[] = [
  {
    regime: 'MEI',
    displayName: 'MEI - Microempreendedor Individual',
    basePrice: 5,
    description: 'Apenas DASN-SIMEI anual',
    obligations: ['DASN-SIMEI'],
    complexityLevel: 'very_low',
  },
  {
    regime: 'SimplesNacional',
    displayName: 'Simples Nacional',
    basePrice: 25,
    description: 'eSocial + EFD-ICMS/IPI (se aplicável)',
    obligations: ['eSocial', 'EFD-ICMS/IPI', 'DEFIS'],
    complexityLevel: 'low',
  },
  {
    regime: 'LucroPresumido',
    displayName: 'Lucro Presumido',
    basePrice: 70,
    description: 'ECD + ECF + EFD-Contribuições + Reinf + eSocial',
    obligations: ['ECD', 'ECF', 'EFD-Contribuições', 'Reinf', 'eSocial'],
    complexityLevel: 'medium',
  },
  {
    regime: 'LucroReal',
    displayName: 'Lucro Real',
    basePrice: 120,
    description: 'Todos os módulos SPED + controles contábeis e fiscais completos',
    obligations: [
      'ECD',
      'ECF',
      'EFD-ICMS/IPI',
      'EFD-Contribuições',
      'Reinf',
      'eSocial',
      'DCTF',
      'EFD-IRPJ',
    ],
    complexityLevel: 'high',
  },
];

// Volume-based tiers for Notas Fiscais
// Foco em escritórios de contabilidade pequenos/médios
// Preços aumentam proporcionalmente ao volume de processamento
export const NOTAS_FISCAIS_TIERS: PricingTier[] = [
  { minValue: 0, maxValue: 100, additionalCost: 0, label: 'Até 100 notas/mês (micro)' },
  { minValue: 101, maxValue: 500, additionalCost: 30, label: '101-500 notas/mês (pequeno)' },
  { minValue: 501, maxValue: 1000, additionalCost: 80, label: '501-1.000 notas/mês' },
  { minValue: 1001, maxValue: 3000, additionalCost: 200, label: '1.001-3.000 notas/mês (médio)' },
  { minValue: 3001, maxValue: 5000, additionalCost: 400, label: '3.001-5.000 notas/mês' },
  { minValue: 5001, maxValue: 10000, additionalCost: 800, label: '5.001-10.000 notas/mês (alto volume)' },
  { minValue: 10001, maxValue: 20000, additionalCost: 1500, label: '10.001-20.000 notas/mês (muito alto)' },
  { minValue: 20001, maxValue: null, additionalCost: 3000, label: 'Acima de 20.000 notas/mês (enterprise)' },
];

// Volume-based tiers for Items per Invoice
// Escala baseada em complexidade de processamento
// Preços aumentam significativamente para volumes extremos
export const ITENS_POR_NOTA_TIERS: PricingTier[] = [
  { minValue: 0, maxValue: 10, additionalCost: 0, label: 'Até 10 itens/nota (simples)' },
  { minValue: 11, maxValue: 25, additionalCost: 50, label: '11-25 itens/nota (normal)' },
  { minValue: 26, maxValue: 50, additionalCost: 150, label: '26-50 itens/nota (médio)' },
  { minValue: 51, maxValue: 100, additionalCost: 350, label: '51-100 itens/nota (complexo)' },
  { minValue: 101, maxValue: 200, additionalCost: 700, label: '101-200 itens/nota (muito complexo)' },
  { minValue: 201, maxValue: 500, additionalCost: 1400, label: '201-500 itens/nota (alto volume)' },
  { minValue: 501, maxValue: null, additionalCost: 2500, label: 'Acima de 500 itens/nota (carga extrema)' },
];

// Employee count tiers (eSocial)
export const FUNCIONARIOS_TIERS: PricingTier[] = [
  { minValue: 0, maxValue: 0, additionalCost: 0, label: 'Sem funcionários' },
  { minValue: 1, maxValue: 5, additionalCost: 15, label: '1-5 funcionários' },
  { minValue: 6, maxValue: 10, additionalCost: 30, label: '6-10 funcionários' },
  { minValue: 11, maxValue: null, additionalCost: 50, label: 'Acima de 10 funcionários' },
];

export interface CompanyCountByRegime {
  MEI: number;
  SimplesNacional: number;
  LucroPresumido: number;
  LucroReal: number;
}

export interface RegimePricingDetail {
  regime: RegimeTributario;
  companyCount: number;
  basePrice: number;
  totalBasePrice: number; // basePrice × companyCount
}

export interface PricingBreakdown {
  regime: RegimeTributario;
  regimeBasePrice: number;
  notasFiscaisAdditional: number;
  itensPerNotaAdditional: number;
  funcionariosAdditional: number;
  totalMonthly: number;
  volumeFactors: VolumeFactors;
}

export interface MultiRegimePricingBreakdown {
  regimeDetails: RegimePricingDetail[];
  totalCompanies: number;
  totalRegimeBaseCost: number;
  notasFiscaisAdditional: number;
  itensPerNotaAdditional: number;
  funcionariosAdditional: number;
  totalMonthly: number;
  volumeFactors: VolumeFactors;
}

/**
 * Calculate monthly price based on regime and volume factors
 */
export function calculatePricing(
  regime: RegimeTributario,
  volumeFactors: VolumeFactors
): PricingBreakdown {
  // Get regime base price
  const regimeConfig = REGIME_BASE_PRICES.find((r) => r.regime === regime);
  if (!regimeConfig) {
    throw new Error(`Invalid regime: ${regime}`);
  }

  const regimeBasePrice = regimeConfig.basePrice;

  // Calculate Notas Fiscais additional cost
  const notasTier = NOTAS_FISCAIS_TIERS.find(
    (tier) =>
      volumeFactors.notasFiscaisMensal >= tier.minValue &&
      (tier.maxValue === null || volumeFactors.notasFiscaisMensal <= tier.maxValue)
  );
  const notasFiscaisAdditional = notasTier?.additionalCost || 0;

  // Calculate Items per Nota additional cost
  const itensTier = ITENS_POR_NOTA_TIERS.find(
    (tier) =>
      volumeFactors.itensMediaPorNota >= tier.minValue &&
      (tier.maxValue === null || volumeFactors.itensMediaPorNota <= tier.maxValue)
  );
  const itensPerNotaAdditional = itensTier?.additionalCost || 0;

  // Calculate Funcionarios additional cost
  const funcionariosTier = FUNCIONARIOS_TIERS.find(
    (tier) =>
      volumeFactors.funcionariosAtivos >= tier.minValue &&
      (tier.maxValue === null || volumeFactors.funcionariosAtivos <= tier.maxValue)
  );
  const funcionariosAdditional = funcionariosTier?.additionalCost || 0;

  // Total monthly cost
  const totalMonthly =
    regimeBasePrice +
    notasFiscaisAdditional +
    itensPerNotaAdditional +
    funcionariosAdditional;

  return {
    regime,
    regimeBasePrice,
    notasFiscaisAdditional,
    itensPerNotaAdditional,
    funcionariosAdditional,
    totalMonthly,
    volumeFactors,
  };
}

/**
 * Get tier info for display
 */
export function getTierInfo(value: number, tiers: PricingTier[]): PricingTier | undefined {
  return tiers.find(
    (tier) => value >= tier.minValue && (tier.maxValue === null || value <= tier.maxValue)
  );
}

/**
 * Calculate pricing for multiple regimes (accounting firm portfolio)
 */
export function calculateMultiRegimePricing(
  companyCounts: CompanyCountByRegime,
  volumeFactors: VolumeFactors
): MultiRegimePricingBreakdown {
  const regimeDetails: RegimePricingDetail[] = [];
  let totalRegimeBaseCost = 0;

  // Calculate cost for each regime
  Object.entries(companyCounts).forEach(([regime, count]) => {
    if (count > 0) {
      const regimeConfig = REGIME_BASE_PRICES.find((r) => r.regime === regime);
      if (regimeConfig) {
        const totalBasePrice = regimeConfig.basePrice * count;
        totalRegimeBaseCost += totalBasePrice;

        regimeDetails.push({
          regime: regime as RegimeTributario,
          companyCount: count,
          basePrice: regimeConfig.basePrice,
          totalBasePrice,
        });
      }
    }
  });

  // Calculate total companies
  const totalCompanies = Object.values(companyCounts).reduce((sum, count) => sum + count, 0);

  // Calculate volume-based costs (applies to total portfolio)
  const notasTier = NOTAS_FISCAIS_TIERS.find(
    (tier) =>
      volumeFactors.notasFiscaisMensal >= tier.minValue &&
      (tier.maxValue === null || volumeFactors.notasFiscaisMensal <= tier.maxValue)
  );
  const notasFiscaisAdditional = notasTier?.additionalCost || 0;

  const itensTier = ITENS_POR_NOTA_TIERS.find(
    (tier) =>
      volumeFactors.itensMediaPorNota >= tier.minValue &&
      (tier.maxValue === null || volumeFactors.itensMediaPorNota <= tier.maxValue)
  );
  const itensPerNotaAdditional = itensTier?.additionalCost || 0;

  const funcionariosTier = FUNCIONARIOS_TIERS.find(
    (tier) =>
      volumeFactors.funcionariosAtivos >= tier.minValue &&
      (tier.maxValue === null || volumeFactors.funcionariosAtivos <= tier.maxValue)
  );
  const funcionariosAdditional = funcionariosTier?.additionalCost || 0;

  // Total monthly cost
  const totalMonthly =
    totalRegimeBaseCost +
    notasFiscaisAdditional +
    itensPerNotaAdditional +
    funcionariosAdditional;

  return {
    regimeDetails,
    totalCompanies,
    totalRegimeBaseCost,
    notasFiscaisAdditional,
    itensPerNotaAdditional,
    funcionariosAdditional,
    totalMonthly,
    volumeFactors,
  };
}

/**
 * Format currency in BRL
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}
